import type { QueryClient } from '@tanstack/react-query'

import { playgroundKeys } from '../shared/queries/usePlaygroundImages'
import { playgroundVideoKeys } from '../shared/queries/usePlaygroundVideos'
import { workspaceMediaItemKeys } from '../shared/queries/useWorkspaceMediaItems'

export interface PlaygroundNotification {
  workspaceId: string
  activityId: string
  status: string
  generatedCount: number
  error: string | null
}

export function parsePlaygroundNotification(
  message: unknown,
  requireError = false,
): PlaygroundNotification {
  const result: unknown = typeof message === 'string' ? JSON.parse(message) : message
  if (
    typeof result !== 'object' ||
    result === null ||
    !('WorkspaceId' in result) ||
    typeof result.WorkspaceId !== 'string' ||
    !('ActivityId' in result) ||
    typeof result.ActivityId !== 'string' ||
    !('Status' in result) ||
    typeof result.Status !== 'string' ||
    !('GeneratedCount' in result) ||
    typeof result.GeneratedCount !== 'number' ||
    (requireError && !('Error' in result)) ||
    ('Error' in result && typeof result.Error !== 'string' && result.Error !== null)
  ) {
    throw new Error('Invalid playground generation notification')
  }
  const error = 'Error' in result && typeof result.Error === 'string' ? result.Error : null
  return {
    workspaceId: result.WorkspaceId,
    activityId: result.ActivityId,
    status: result.Status,
    generatedCount: result.GeneratedCount,
    error,
  }
}

export function handlePlaygroundNotification(queryClient: QueryClient, message: unknown) {
  const result = parsePlaygroundNotification(message)
  void queryClient.invalidateQueries({ queryKey: playgroundKeys.workspace(result.workspaceId) })
  void queryClient.invalidateQueries({
    queryKey: workspaceMediaItemKeys.workspace(result.workspaceId),
  })
  void queryClient.invalidateQueries({ queryKey: ['queue'] })
  return result
}

export function handlePlaygroundVideoNotification(queryClient: QueryClient, message: unknown) {
  const result = parsePlaygroundNotification(message, true)
  void queryClient.invalidateQueries({
    queryKey: playgroundVideoKeys.active(result.workspaceId),
  })
  void queryClient.invalidateQueries({
    queryKey: playgroundVideoKeys.status(result.workspaceId, result.activityId),
  })
  void queryClient.invalidateQueries({
    queryKey: playgroundVideoKeys.videos(result.workspaceId),
  })
  void queryClient.invalidateQueries({
    queryKey: workspaceMediaItemKeys.workspace(result.workspaceId),
  })
  void queryClient.invalidateQueries({ queryKey: ['queue'] })
  return result
}
